import { claudeAgentApi } from "@/lib/api/claude-agent";
import { useSlideStore } from "@/stores/slide-store";
import type { TransitionType } from "@/stores/slide-store";

interface FetchSlideManifestOptions {
  devContainerId: string;
  getIdToken: () => Promise<string | null>;
  // When true (default), `manifest.config` overwrites
  // `presentationConfig` in the store. The presenter window's 5s poll
  // passes `false` because `autoplay` / `interval` / `loop` there are
  // session-controlled by the ▶/⏸ button — letting the manifest
  // default (typically `autoplay: false`) write back every poll kills
  // the live autoplay scheduler. Structural fields (`aspectRatio`,
  // `transition`) are still applied via the dedicated branch below.
  syncPresentationConfig?: boolean;
}

// Manifest lives at the new path for projects cloned from the current
// template, or the legacy path for projects created before the relocation.
// Prefer new; fall back to legacy on 404. Anything else propagates.
const NEW_MANIFEST_PATH = "/workspaces/src/data/slides.json";
const LEGACY_MANIFEST_PATH = "/workspaces/src/slides/slides.json";

async function readManifestWithFallback(
  token: string,
  devContainerId: string,
): Promise<{ content: string }> {
  try {
    return await claudeAgentApi.readFile(
      token,
      devContainerId,
      NEW_MANIFEST_PATH,
    );
  } catch (error: unknown) {
    const status = (error as { response?: { status?: number } })?.response
      ?.status;
    if (status !== 404) throw error;
    return await claudeAgentApi.readFile(
      token,
      devContainerId,
      LEGACY_MANIFEST_PATH,
    );
  }
}

/**
 * Fetch slides.json from the dev container and sync to Zustand store.
 * Standalone function (not a hook) — callable from event handlers.
 *
 * Returns true if this is a slides project, false otherwise.
 */
export async function fetchSlideManifest({
  devContainerId,
  getIdToken,
  syncPresentationConfig = true,
}: FetchSlideManifestOptions): Promise<boolean> {
  try {
    const token = await getIdToken();
    if (!token) return false;

    const { content } = await readManifestWithFallback(token, devContainerId);

    const manifest = JSON.parse(content);
    const slides = manifest?.slides;

    if (!Array.isArray(slides) || slides.length === 0) {
      useSlideStore.getState().setIsSlideProject(false);
      return false;
    }

    // Sort by position field if present (set by drag-and-drop reorder)
    const sorted = [...slides].sort((a, b) => {
      if (a.position !== undefined && b.position !== undefined) {
        return a.position - b.position;
      }
      return 0;
    });

    const store = useSlideStore.getState();
    store.setIsSlideProject(true);
    store.setSlides(sorted);

    // Sync presentation config from slides.json. `aspectRatio` and
    // `transition` are structural — always synced so layout / viewport
    // stays correct even on refresh polls. Session-controllable fields
    // (`autoplay`, `interval`, `loop`) are only synced when the caller
    // opts in, so the presenter window's poll doesn't trample live UI
    // state set by the ▶/⏸ button.
    if (manifest.config) {
      const { transition, aspectRatio, ...presentationFields } =
        manifest.config;
      const next: Record<string, unknown> = {};
      if (transition) next.transition = transition as TransitionType;
      if (aspectRatio === "16:9" || aspectRatio === "4:3") {
        next.aspectRatio = aspectRatio;
      }
      if (syncPresentationConfig) {
        Object.assign(next, presentationFields);
      }
      if (Object.keys(next).length > 0) {
        store.setPresentationConfig(next);
      }
    }

    return true;
  } catch (error: unknown) {
    // Only mark as non-slides project on 404 (file doesn't exist) or JSON parse error
    // Transient errors (401, 500, network) should not flip the flag
    const status = (error as { response?: { status?: number } })?.response
      ?.status;
    if (status === 404 || error instanceof SyntaxError) {
      useSlideStore.getState().setIsSlideProject(false);
    }
    return false;
  }
}
